import type { AuthMembership, AuthUser, Permission, Role } from '@saniplus/contracts';

import type { AuthState } from './authSlice';

/** The minimum a state must contain for these selectors to apply. */
export interface WithAuthState {
  readonly auth: AuthState;
}

export const selectAuthStatus = (state: WithAuthState): AuthState['status'] => state.auth.status;
export const selectCurrentUser = (state: WithAuthState): AuthUser | null => state.auth.user;
export const selectAccessToken = (state: WithAuthState): string | null => state.auth.accessToken;
export const selectMemberships = (state: WithAuthState): AuthMembership[] => state.auth.memberships;
export const selectPermissions = (state: WithAuthState): Permission[] => state.auth.permissions;
export const selectMustChangePin = (state: WithAuthState): boolean => state.auth.mustChangePin;
export const selectAuthError = (state: WithAuthState): AuthState['error'] => state.auth.error;

/**
 * The caller's one system-wide role (Phase 4: `users.role`). This is the only
 * authority field the session carries — a membership is scope, and nothing
 * about *what* a person may do is derived from one. `null` while anonymous.
 */
export const selectRole = (state: WithAuthState): Role | null => state.auth.user?.role ?? null;

/** Convenience for the portal split and the platform-only screens. */
export const selectIsPlatformAdmin = (state: WithAuthState): boolean =>
  state.auth.user?.role === 'platform_admin';

/**
 * A *verified* session holding a usable credential. Unchanged, and
 * deliberately `false` for an offline rehydration — anything that is about to
 * call the API, or about to decide that a token exists, asks this one.
 */
export const selectIsAuthenticated = (state: WithAuthState): boolean =>
  state.auth.status === 'authenticated' && state.auth.accessToken !== null;

/** Rehydrated from the offline identity snapshot; nothing is verified. */
export const selectIsOfflineSession = (state: WithAuthState): boolean =>
  state.auth.status === 'offline';

/**
 * "Is there someone to render this shell for" — the route guard's question,
 * which is not `selectIsAuthenticated`'s.
 *
 * A worker who reopens the installed PWA in a plant with no signal has no
 * token and never will until they walk into coverage; sending them to a login
 * screen that needs the network they lack strands the punch already queued
 * under their user id. Screens still gate on `selectIsAuthenticated` for
 * anything that requires a live credential.
 */
export const selectHasSession = (state: WithAuthState): boolean =>
  selectIsAuthenticated(state) || selectIsOfflineSession(state);

/** `true` until `bootstrapSession` has settled — the router must wait. */
export const selectIsSessionUnresolved = (state: WithAuthState): boolean =>
  state.auth.status === 'idle' || state.auth.status === 'bootstrapping';

/**
 * The single gate for every navigation item and action button (D-02: "nav
 * gates on `permissions[]`"). The server enforces the same rule; this only
 * stops the UI offering something that would 403.
 */
export function hasPermission(state: WithAuthState, permission: Permission): boolean {
  return state.auth.permissions.includes(permission);
}

/** Curried form for `useAppSelector(selectHasPermission('shift:write'))`. */
export const selectHasPermission =
  (permission: Permission) =>
  (state: WithAuthState): boolean =>
    hasPermission(state, permission);

/**
 * AZ-1: never send a `contractId` the session does not hold a membership for.
 *
 * Memberships carry scope, not authority: this reads `contractId` only, and
 * whether the caller may *act* on that contract is `selectRole` /
 * `hasPermission`'s question. A platform admin's scope is every contract, but
 * the wire still lists their memberships, so this stays a plain lookup.
 */
export function isContractInScope(state: WithAuthState, contractId: string): boolean {
  return state.auth.memberships.some((membership) => membership.contractId === contractId);
}
